import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DashboardService } from './dashboards/dashboard.service';
import { AdmClienteFeatureService } from 'projects/adm/src/app/shared/services/adm-cliente-feature.service';
import { ClientFeatures } from 'projects/adm/src/app/modules/solvace-customer/models/client-features.interface';
import { SvcDataCacheService } from '@solvace/core/services/cache/svc-data-cache.service';
import { SvcCacheName } from '@solvace/core/services/cache/svc-cache-name.enum';
import { StaticApplicationId } from '@solvace/core/Constants/static-application-id.enum';
import { ContractedModules } from './models/contracted-modules.interface';
import { ParamsKeyEnum } from './enums/params-key.enum';

@Injectable({ providedIn: 'root' })
export class MainService {
  private readonly admClienteFeatureService = inject(AdmClienteFeatureService);
  private readonly dataCacheService = inject(SvcDataCacheService);
  private readonly dashboardService = inject(DashboardService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly applicationIds = [
    StaticApplicationId.training,
    StaticApplicationId.praise,
    StaticApplicationId.projects,
    StaticApplicationId.kaizen,
    StaticApplicationId.defectTag,
    StaticApplicationId.defectTagLegacy,
    StaticApplicationId.lil,
    StaticApplicationId.myApprovals,
    StaticApplicationId.communication,
  ];

  private menus: any[] = [];
  private functionalityHidden = false;

  private contractedModules: ContractedModules = {
    training: true,
    praise: true,
    projects: true,
    kaizen: true,
    defectTag: true,
    lil: true,
    myApprovals: true,
    communication: true,
  };

  private readonly reloadMenuSubject = new BehaviorSubject<ContractedModules>(this.contractedModules);
  public readonly reloadMenu$ = this.reloadMenuSubject.asObservable();
  public canHideMenu = new BehaviorSubject<boolean>(false);

  /** Carrega os indicadores do usuário */
  loadUserIndicators(): void {
    this.dashboardService.getUserIndicators();
  }

  /** Consulta os módulos contratados */
  private getContractedModules(): Observable<ClientFeatures[]> {
    return this.admClienteFeatureService.getTreeTable();
  }

  /** Atualiza os módulos contratados e menus disponíveis */
  public checkContractedModules(): void {
    this.getContractedModules()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((features: ClientFeatures[]) => {
          this.functionalityHidden = features.find((item) => item.paramKey === ParamsKeyEnum.HIDE_HOME_ELEMENTS).paramValue ?? false;
          const hideInactiveMenus = features.find((item) => item.paramKey === ParamsKeyEnum.HIDE_INACTIVE_MENUS).paramValue ?? false;
          this.canHideMenu.next(hideInactiveMenus);
          this.reloadMenuSubject.next(this.resolveContractedModules());
        })
      )
      .subscribe();

    this.loadMenusFromCache();
  }

  public get isHideHomeElementsOn(): boolean {
    return this.functionalityHidden;
  }

  public getActiveApplicationIds(): Set<string> {
    const cachedMenus: any[] = this.dataCacheService.get(SvcCacheName.APPS_MENU) ?? [];
    const activeIds = cachedMenus
      .flatMap((group: any) => group.menuItems ?? [])
      .filter((item: any) => item.isActive)
      .map((item: any) => (item.applicationId as string)?.toUpperCase())
      .filter(Boolean);
    return new Set<string>(activeIds);
  }

  /** Recupera os menus do cache */
  private loadMenusFromCache(): void {
    const cachedMenus: any = this.dataCacheService.get(SvcCacheName.APPS_MENU) ?? [];
    this.menus = cachedMenus.flatMap((group: any) => group.menuItems ?? []).filter((item: any) => this.applicationIds.includes(item.applicationId));
  }

  /** Define quais módulos estão habilitados */
  private resolveContractedModules(): ContractedModules {
    if (!this.functionalityHidden || !this.menus?.length) {
      return this.contractedModules;
    }

    const checkModule = (appId: StaticApplicationId) => this.menus.some((item) => item.applicationId === appId && item.isActive);

    return {
      training: checkModule(StaticApplicationId.training),
      praise: checkModule(StaticApplicationId.praise),
      projects: checkModule(StaticApplicationId.projects),
      kaizen: checkModule(StaticApplicationId.kaizen),
      defectTag: checkModule(StaticApplicationId.defectTag) || checkModule(StaticApplicationId.defectTagLegacy),
      lil: checkModule(StaticApplicationId.lil),
      myApprovals: checkModule(StaticApplicationId.myApprovals),
      communication: checkModule(StaticApplicationId.communication),
    };
  }
}
